import {useBookmarksStore} from '@/ts/store/bookmarks-store';
import {useApi} from '@/ts/composables/stateful/use-api';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';
import {extractError} from '@/ts/composables/pure/use-loading-data-ref';
import {useI18n} from 'vue-i18n';

/** Provides methods to fetch and toggle content bookmarks, syncing with the bookmarks store */
export const useBookmarks = (): {
  fetchBookmarks(): Promise<void>;
  updateBookmark(bookmark: ComponentContent, isBookmarked: boolean): Promise<void>
} => {
  const store = useBookmarksStore();
  const api = useApi();
  const {t} = useI18n();

  const fetchBookmarks = async(): Promise<void> => {
    store.setLoading(true);
    store.setError(null);
    try {
      const data = await api.content.getBookmarks();
      store.setBookmarks(data);
    } catch (e) {
      store.setError(extractError(e, t));
    } finally {
      store.setLoading(false);
    }
  };

  const updateBookmark = async(bookmark: ComponentContent, isBookmarked: boolean): Promise<void> => {
    await api.me.postBlockContentBookmark(bookmark.id, {isBookmarked});

    if (isBookmarked) {
      store.addBookmark(bookmark);
    } else {
      store.removeBookmark(bookmark);
    }
  };

  return {
    fetchBookmarks,
    updateBookmark,
  };
};
